import { useMemo } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Trans, useTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled from 'styled-components';

import { useDate } from '~/libs/hooks/useDate';
import { useWeather } from '~/modules/weather/useWeather';
import { useNavigate } from '~/router';

import { ReportListModel } from '../../libs/types';
import NoReportImage from '../NoReportImage';
import { getProcessText } from '../ReportListFilter/ReportListFilter.helper';

export type ReportsItemProps = {
  data: ReportListModel;
  showProjectName?: boolean;
};

export const ReportsItem = (props: ReportsItemProps) => {
  const { data, showProjectName } = props;
  const { t } = useTranslation('report');
  const { getWeatherIcon, getTemperatureUnitText } = useWeather();
  const navigate = useNavigate();
  const { formatDate } = useDate();

  const renderWeather = () => {
    if (!data.weather) return null;
    const unitsText = getTemperatureUnitText(data.temperature?.units);

    return (
      <Box display="flex" alignItems="center">
        {getWeatherIcon(data.weather, {
          style: { width: '1rem', height: '1rem' },
        })}

        <WeatherTemperature>
          <span className="low">
            <span>{data.temperature?.low}</span>
            <span className="units">{unitsText}</span>
          </span>
          <span>{' / '}</span>
          <span className="high">
            <span>{data.temperature?.high}</span>
            <span className="units">{unitsText}</span>
          </span>
        </WeatherTemperature>
      </Box>
    );
  };

  const flattedPhotos = useMemo(() => {
    return data?.works?.flatMap((e) => e.photos).filter((e) => e) || [];
  }, [data.works]);

  const generateThumbnail = () => {
    const getClassName = () => {
      if (flattedPhotos?.length > 4) return 'type-A';
      switch (flattedPhotos?.length) {
        case 4:
          return 'type-A';
        case 3:
          return 'type-B';
        case 2:
          return 'type-C';
        case 1:
        default:
          return 'type-D';
      }
    };

    const className = getClassName();

    const handleClickItem = () => {
      navigate('/projects/:projectId/reports/:reportId', {
        params: { projectId: data.project_id, reportId: data._id },
        state: { isFromList: true },
      });
    };

    return (
      <ThumbnailAnchor onClick={handleClickItem} className={className}>
        <Box className="grid" position="absolute" top={0} left={0} width="100%" height="100%">
          {!flattedPhotos.length && <NoReportImage noTextContent height="100%" />}
          {flattedPhotos.slice(0, 4).map((e, index) => {
            const isLastForTypeA =
              className === 'type-A' && index === 3 && flattedPhotos.length > 4;

            return (
              <ThumbnailCoverStyled key={e._id} className="grid-item">
                <LazyLoadImage
                  src={e.thumbnailUrl || e.url}
                  width="100%"
                  height="100%"
                  effect="opacity"
                />
                {isLastForTypeA && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    height="100%"
                    color="white"
                    fontSize="18px"
                    fontWeight={500}
                    opacity={0.7}
                    background="#0000004D"
                  >
                    +
                    <Trans
                      t={t}
                      i18nKey="report:m_report_thumbnail_length_text"
                      values={{
                        length: flattedPhotos.length - 4,
                      }}
                    />
                  </Box>
                )}
              </ThumbnailCoverStyled>
            );
          })}
        </Box>
        {data.dayoff && <DayoffTag>{t('report:dayoff')}</DayoffTag>}
      </ThumbnailAnchor>
    );
  };

  return (
    <ListItem>
      {showProjectName && !!data?.project && (
        <Box
          color="var(--Gray-400)"
          fontSize="0.875rem"
          fontWeight={500}
          lineHeight="1.125rem"
          mb="0.25rem"
        >
          {data?.project.title}
        </Box>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="6px">
        <Box color="#2C384A" fontSize="16px" fontWeight={600}>
          {formatDate(data.workDate)}
        </Box>
        {renderWeather()}
      </Box>
      {generateThumbnail()}

      <TagWrap>
        {data?.works?.map((work) => {
          if (!work.process?.title) return null;
          return (
            <TagItem key={work.process_id}>
              {getProcessText(work?.process?.title, work?.task?.text)}
            </TagItem>
          );
        })}
      </TagWrap>
    </ListItem>
  );
};

const DayoffTag = styled.div`
  position: absolute;
  z-index: 1;
  top: 8px;
  left: 8px;

  display: flex;
  align-items: center;
  padding: 0 5px;
  gap: 8px;
  height: 24px;

  color: white;
  background: #ff8f1f;
  font-weight: 500;
  font-size: 14px;
  border-radius: 4px;
`;

const ListItem = styled.div`
  padding: 0 1rem;
  padding-bottom: 24px;
`;

const WeatherTemperature = styled.span`
  font-size: 14px;
  font-weight: 400;
  margin-left: 5px;

  color: #808892;

  .units {
    font-size: 12px;
  }
  .low {
    color: #1982c4;
  }
  .high {
    color: #f94144;
  }
`;

const ThumbnailAnchor = styled.a`
  position: relative;
  display: block;
  padding-bottom: 49%;

  & > .grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 8px;
  }

  &.type-A > .grid {
    grid-template-columns: repeat(3, 2fr, 1fr, 1fr);
    grid-template-rows: repeat(2, 1fr);
    .grid-item:nth-child(1) {
      grid-column: 1 / 3;
      grid-row: 1 / 3;
    }
    .grid-item:nth-child(2) {
      grid-column: 3 / 5;
    }
  }
  &.type-B > .grid {
    grid-template-columns: repeat(2, 1fr);
    .grid-item:nth-child(1) {
      grid-column: 1 / 2;
      grid-row: 1 / 3;
    }
  }
  &.type-C > .grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
  }
  &.type-D > .grid {
    /* display: block; */
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }
`;

const ThumbnailCoverStyled = styled.div`
  position: relative;
  background-color: #d9d9d9;
  overflow: hidden;

  .lazy-load-image-background.opacity {
    opacity: 0;
    transition: opacity 0.3s;
    &.lazy-load-image-loaded {
      opacity: 1;
    }
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const TagWrap = styled.div`
  position: relative;
  margin-left: -1rem;
  margin-right: -1rem;
  margin-top: 8px;
  display: flex;
  padding-left: 1rem;
  padding-right: 1rem;
  gap: 4px;
  white-space: nowrap;
  overflow-x: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const TagItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 23px;
  border: 1px solid #d9d9d9;
  color: #666666;
  background-color: white;
  border-radius: 4px;
`;
